/** sessionStorage key for the path to resume after login. */
export const LOGIN_NEXT_STORAGE_KEY = "loginNext";

const MAX_NEXT_LENGTH = 512;

/**
 * Accepts only same-origin relative paths. Rejects protocol-relative URLs,
 * schemes, and the login screens themselves (to avoid redirect loops).
 */
export function sanitizeLoginNext(raw: string | null | undefined): string | null {
    if (raw == null) {
        return null;
    }
    const trimmed = raw.trim();
    if (!trimmed || trimmed.length > MAX_NEXT_LENGTH) {
        return null;
    }

    let decoded = trimmed;
    try {
        decoded = decodeURIComponent(trimmed);
    } catch {
        return null;
    }

    if (!decoded.startsWith("/") || decoded.startsWith("//") || decoded.startsWith("/\\")) {
        return null;
    }
    if (decoded.includes("\\") || /[\u0000-\u001F\u007F]/.test(decoded)) {
        return null;
    }
    if (/^[a-zA-Z][a-zA-Z+.-]*:/.test(decoded)) {
        return null;
    }

    const pathOnly = decoded.split(/[?#]/, 1)[0];
    if (pathOnly === "/login" || pathOnly.startsWith("/login/")) {
        return null;
    }
    return decoded;
}

export function persistLoginNext(path: string | null | undefined): string | null {
    const next = sanitizeLoginNext(path);
    if (!next) {
        return null;
    }
    try {
        sessionStorage.setItem(LOGIN_NEXT_STORAGE_KEY, next);
    } catch {
        /* private mode / blocked storage */
    }
    return next;
}

export function persistLoginNextFromSearch(search: string): string | null {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    return persistLoginNext(params.get("next"));
}

/**
 * True when the user was sent to /login after a failed auth check.
 * Leftover localStorage + a still-present cookie must not bounce them
 * back to `next` (that remounts login forever: unauthorized → next → login).
 */
export function isForcedLoginSearch(search: string): boolean {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    return params.get("error") === "unauthorized"
        || params.get("session_expired") === "true"
        || params.get("session_changed") === "true";
}

export function consumeLoginNext(): string | null {
    let stored: string | null = null;
    try {
        stored = sessionStorage.getItem(LOGIN_NEXT_STORAGE_KEY);
        sessionStorage.removeItem(LOGIN_NEXT_STORAGE_KEY);
    } catch {
        /* private mode / blocked storage */
    }
    return sanitizeLoginNext(stored);
}

/** Prefer an explicit next, otherwise consume the stored post-login path. */
export function takePostLoginPath(explicitNext?: string | null): string | null {
    return sanitizeLoginNext(explicitNext) ?? consumeLoginNext();
}

export function loginRoleFromSources(sources: {
    profileType?: string | null;
    role?: string | null;
    userType?: string | null;
    isGuardian?: boolean;
    isPolaris?: boolean;
    isAthlete?: boolean;
}): string {
    const fromLogin = (sources.profileType || sources.role || sources.userType || "").toUpperCase();
    if (fromLogin) {
        return fromLogin;
    }
    if (sources.isGuardian) {
        return "GUARDIAN";
    }
    if (sources.isPolaris) {
        return "POLARIS";
    }
    if (sources.isAthlete) {
        return "ATHLETE";
    }
    return "ADMINISTRATOR";
}

function postLoginPathOnly(next: string): string {
    return next.split(/[?#]/, 1)[0];
}

function pathMatchesPrefix(path: string, prefix: string): boolean {
    return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Deep-link `next` is only honoured when it is a page that role can open.
 * A leftover `next=/admin-profile` after an athlete login must not bounce them
 * through an admin route (which then looks like a failed login).
 */
export function isPostLoginNextAllowed(role: string | null | undefined, next: string | null | undefined): boolean {
    const sanitized = sanitizeLoginNext(next);
    if (!sanitized) {
        return false;
    }
    const path = postLoginPathOnly(sanitized);
    const r = (role || "").toUpperCase();
    if (/^\/(support-tickets|calendar|drive|ai-assistant)(\/|$)/.test(path)) {
        return true;
    }
    if (r === "ATHLETE") {
        return pathMatchesPrefix(path, "/athlete");
    }
    if (r === "GUARDIAN") {
        return pathMatchesPrefix(path, "/athlete") || pathMatchesPrefix(path, "/guardian");
    }
    if (r === "POLARIS") {
        return pathMatchesPrefix(path, "/polaris-admin");
    }
    return !pathMatchesPrefix(path, "/athlete")
        && !pathMatchesPrefix(path, "/guardian")
        && !pathMatchesPrefix(path, "/polaris-admin");
}

export function defaultPostLoginPath(role: string | null | undefined, hasProfile?: boolean): string {
    const r = (role || "").toUpperCase();
    if (r === "GUARDIAN") {
        return "/guardian/select";
    }
    if (r === "POLARIS") {
        return "/polaris-admin";
    }
    if (r === "ATHLETE") {
        return "/athlete/home";
    }
    if (hasProfile) {
        return "/admin-profile";
    }
    return "/add-admin-profile";
}

export function resolvePostLoginPath(
    role: string | null | undefined,
    next: string | null | undefined,
    hasProfile?: boolean,
): string {
    if (next && isPostLoginNextAllowed(role, next)) {
        return next;
    }
    return defaultPostLoginPath(role, hasProfile);
}

export function unauthorizedLoginPath(from: {
    pathname: string;
    search?: string;
    hash?: string;
}): string {
    const next = persistLoginNext(`${from.pathname}${from.search ?? ""}${from.hash ?? ""}`);
    const params = new URLSearchParams();
    params.set("error", "unauthorized");
    if (next) {
        params.set("next", next);
    }
    return `/login?${params.toString()}`;
}
